import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Trash2,
  Check,
  Archive,
  RotateCcw,
  LogOut,
  Loader2,
  Mail,
  Phone,
  Globe,
  Building2,
  MapPin,
  X,
  Inbox,
  RefreshCcw,
} from "lucide-react";
import { API, useAuth } from "@/context/AuthContext";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "archived", label: "Archived" },
];

const STATUS_STYLES = {
  new: "bg-[#C8A24B]/[0.12] text-[#C8A24B] border-[#C8A24B]/40",
  contacted: "bg-[#22c55e]/[0.08] text-[#22c55e] border-[#22c55e]/30",
  archived: "bg-[#52525b]/20 text-[#a1a1aa] border-[#3f3f46]",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter && statusFilter !== "all")
        params.status_filter = statusFilter;
      const { data } = await axios.get(`${API}/leads`, { params });
      setLeads(data.leads || []);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [q, statusFilter, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status });
      setLeads((prev) =>
        prev.map((l) => (l.id === id ? { ...l, status } : l))
      );
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      /* ignore */
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      /* ignore */
    }
  };

  const stats = {
    total: leads.length,
    new: leads.filter((l) => (l.status || "new") === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    archived: leads.filter((l) => l.status === "archived").length,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-[#27272a] bg-[#0a0a0a] sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 border border-[#C8A24B]"
              style={{ clipPath: "polygon(50% 0, 100% 100%, 0 100%)" }}
            />
            <div>
              <div className="font-heading text-sm tracking-widest uppercase text-white">
                Admin Console
              </div>
              <div className="text-[9px] tracking-[0.28em] text-[#71717a] uppercase mt-0.5">
                Agent Marketing
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-[#a1a1aa] hidden md:block">
              Signed in as{" "}
              <span className="text-white">{user?.email}</span>
            </div>
            <button
              onClick={async () => {
                await logout();
                navigate("/admin/login");
              }}
              data-testid="admin-logout-button"
              className="btn-outline text-xs py-2 px-3"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-3">
            Growth Plan Inbox
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white leading-tight">
            Leads
          </h1>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} icon={Inbox} />
          <StatCard label="New" value={stats.new} accent />
          <StatCard label="Contacted" value={stats.contacted} />
          <StatCard label="Archived" value={stats.archived} />
        </div>

        {/* Filter bar */}
        <div className="border border-[#27272a] rounded-sm bg-[#0e0e0e] mb-6 p-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 flex items-center gap-2 bg-[#0a0a0a] border border-[#27272a] rounded-sm px-3 py-2 focus-within:border-[#C8A24B]">
            <Search size={14} className="text-[#71717a]" />
            <input
              data-testid="admin-leads-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, company, profession"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-[#52525b]"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="text-[#71717a] hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex gap-1 border border-[#27272a] p-1 rounded-sm bg-[#0a0a0a]">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`admin-filter-${t.key}`}
                onClick={() => setStatusFilter(t.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
                  statusFilter === t.key
                    ? "bg-[#C8A24B] text-[#0a0a0a]"
                    : "text-[#a1a1aa] hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            className="btn-outline text-xs py-2 px-3"
            data-testid="admin-refresh-button"
          >
            <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Leads table */}
        <div
          data-testid="admin-leads-table"
          className="border border-[#27272a] rounded-sm bg-[#0e0e0e] overflow-hidden"
        >
          {loading ? (
            <div className="p-16 flex items-center justify-center">
              <Loader2 size={22} className="text-[#C8A24B] animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox size={28} className="text-[#3f3f46] mx-auto mb-4" />
              <div className="font-heading text-lg text-white mb-2">
                No leads yet
              </div>
              <p className="text-sm text-[#71717a]">
                {q || statusFilter !== "all"
                  ? "Try adjusting your search or filter."
                  : "Growth plan submissions from the contact form will appear here."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="text-left text-[10px] tracking-[0.24em] uppercase text-[#71717a] border-b border-[#27272a]">
                    <th className="px-5 py-4 font-medium">Contact</th>
                    <th className="px-5 py-4 font-medium">Profession</th>
                    <th className="px-5 py-4 font-medium">Stage</th>
                    <th className="px-5 py-4 font-medium">Interested In</th>
                    <th className="px-5 py-4 font-medium">Received</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => {
                    const status = lead.status || "new";
                    return (
                      <tr
                        key={lead.id}
                        data-testid={`admin-lead-row-${lead.id}`}
                        className="border-b border-[#171717] hover:bg-[#131313] transition-colors cursor-pointer"
                        onClick={() => setSelected(lead)}
                      >
                        <td className="px-5 py-4">
                          <div className="font-medium text-white">
                            {lead.name}
                          </div>
                          <div className="text-xs text-[#a1a1aa] mt-0.5">
                            {lead.email}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-[#d4d4d8]">
                          {lead.profession || "-"}
                          <div className="text-xs text-[#71717a]">
                            {lead.company || ""}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-xs text-[#a1a1aa] max-w-[180px]">
                          {lead.current_stage || "-"}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1 max-w-[240px]">
                            {(lead.services_of_interest || [])
                              .slice(0, 3)
                              .map((s) => (
                                <span
                                  key={s}
                                  className="px-2 py-0.5 text-[10px] border border-[#27272a] rounded-sm text-[#a1a1aa]"
                                >
                                  {s}
                                </span>
                              ))}
                            {(lead.services_of_interest || []).length > 3 && (
                              <span className="text-[10px] text-[#71717a]">
                                +{lead.services_of_interest.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-xs text-[#a1a1aa]">
                          {new Date(lead.created_at).toLocaleDateString(
                            "en-CA",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border rounded-sm ${STATUS_STYLES[status]}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td
                          className="px-5 py-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1">
                            {status !== "contacted" && (
                              <ActionBtn
                                testid={`mark-contacted-${lead.id}`}
                                title="Mark contacted"
                                onClick={() =>
                                  updateStatus(lead.id, "contacted")
                                }
                              >
                                <Check size={13} />
                              </ActionBtn>
                            )}
                            {status !== "new" && (
                              <ActionBtn
                                testid={`mark-new-${lead.id}`}
                                title="Mark new"
                                onClick={() => updateStatus(lead.id, "new")}
                              >
                                <RotateCcw size={13} />
                              </ActionBtn>
                            )}
                            {status !== "archived" && (
                              <ActionBtn
                                testid={`archive-${lead.id}`}
                                title="Archive"
                                onClick={() =>
                                  updateStatus(lead.id, "archived")
                                }
                              >
                                <Archive size={13} />
                              </ActionBtn>
                            )}
                            <ActionBtn
                              testid={`delete-${lead.id}`}
                              title="Delete"
                              onClick={() => removeLead(lead.id)}
                              danger
                            >
                              <Trash2 size={13} />
                            </ActionBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Lead detail drawer */}
      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onStatus={updateStatus}
          onDelete={removeLead}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent = false, icon: Icon }) {
  return (
    <div className="border border-[#27272a] bg-[#0e0e0e] p-5 rounded-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a]">
          {label}
        </div>
        {Icon && <Icon size={14} className="text-[#52525b]" />}
      </div>
      <div
        className={`font-heading text-3xl ${accent ? "text-[#C8A24B]" : "text-white"}`}
      >
        {value}
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, title, testid, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-7 h-7 flex items-center justify-center border border-[#27272a] rounded-sm transition-colors ${
        danger
          ? "text-[#a1a1aa] hover:text-[#f87171] hover:border-[#7f1d1d]"
          : "text-[#a1a1aa] hover:text-[#C8A24B] hover:border-[#C8A24B]/40"
      }`}
    >
      {children}
    </button>
  );
}

function LeadDrawer({ lead, onClose, onStatus, onDelete }) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
      data-testid="admin-lead-drawer"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-[#0e0e0e] border-l border-[#27272a] h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0e0e0e] border-b border-[#27272a] p-6 flex items-start justify-between z-10">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-2">
              Lead Detail
            </div>
            <h2 className="font-heading text-2xl text-white">{lead.name}</h2>
            <div className="text-sm text-[#a1a1aa] mt-1">
              {new Date(lead.created_at).toLocaleString()}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 border border-[#27272a] flex items-center justify-center hover:border-[#C8A24B] hover:text-[#C8A24B] text-white"
            data-testid="admin-lead-drawer-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact */}
          <Section title="Contact">
            <Row icon={Mail} value={lead.email} href={`mailto:${lead.email}`} />
            {lead.phone && (
              <Row icon={Phone} value={lead.phone} href={`tel:${lead.phone}`} />
            )}
            {lead.company && <Row icon={Building2} value={lead.company} />}
            {lead.location && <Row icon={MapPin} value={lead.location} />}
            {lead.website && (
              <Row
                icon={Globe}
                value={lead.website}
                href={
                  lead.website.startsWith("http")
                    ? lead.website
                    : `https://${lead.website}`
                }
              />
            )}
          </Section>

          <Section title="Business">
            <Field label="Profession" value={lead.profession} />
            <Field label="Current stage" value={lead.current_stage} />
            <Field label="Budget range" value={lead.budget_range} />
            <Field label="Timeline" value={lead.timeline} />
            <Field label="Preferred contact" value={lead.preferred_contact} />
          </Section>

          <Section title="Services of interest">
            <div className="flex flex-wrap gap-2">
              {(lead.services_of_interest || []).length ? (
                lead.services_of_interest.map((s) => (
                  <span
                    key={s}
                    className="px-3 py-1.5 text-xs border border-[#27272a] rounded-sm text-[#d4d4d8]"
                  >
                    {s}
                  </span>
                ))
              ) : (
                <div className="text-sm text-[#71717a]">Not specified</div>
              )}
            </div>
          </Section>

          {lead.help_needed && (
            <Section title="What they need help with">
              <p className="text-[#d4d4d8] whitespace-pre-wrap leading-relaxed">
                {lead.help_needed}
              </p>
            </Section>
          )}
          {lead.notes && (
            <Section title="Notes">
              <p className="text-[#d4d4d8] whitespace-pre-wrap leading-relaxed">
                {lead.notes}
              </p>
            </Section>
          )}

          <div className="border-t border-[#27272a] pt-6 flex flex-wrap gap-2">
            <button
              onClick={() => onStatus(lead.id, "contacted")}
              className="btn-outline text-xs py-2 px-3"
              data-testid="drawer-mark-contacted"
            >
              <Check size={13} /> Mark contacted
            </button>
            <button
              onClick={() => onStatus(lead.id, "archived")}
              className="btn-outline text-xs py-2 px-3"
              data-testid="drawer-archive"
            >
              <Archive size={13} /> Archive
            </button>
            <button
              onClick={() => onDelete(lead.id)}
              className="btn-outline text-xs py-2 px-3 text-[#f87171] border-[#7f1d1d]/50 hover:border-[#f87171]"
              data-testid="drawer-delete"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-3">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, value, href }) {
  const inner = (
    <>
      <Icon size={14} className="text-[#C8A24B] flex-shrink-0" />
      <span className="text-[#d4d4d8] break-all">{value}</span>
    </>
  );
  if (href)
    return (
      <a
        href={href}
        className="flex items-center gap-3 hover:text-[#C8A24B]"
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
      >
        {inner}
      </a>
    );
  return <div className="flex items-center gap-3">{inner}</div>;
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[130px_1fr] gap-2 text-sm">
      <div className="text-[#71717a]">{label}</div>
      <div className="text-white">{value}</div>
    </div>
  );
}
